import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, CheckCircle2, XCircle, KeyRound, FileText } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { AdManager } from "../components/AdManager";

const KEY_STORAGE = "ditch_admin_key";

export default function Admin() {
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem(KEY_STORAGE) || "");
  const [unlocked, setUnlocked] = useState(!!sessionStorage.getItem(KEY_STORAGE));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const unlock = () => {
    if (!adminKey.trim()) return toast.error("Enter your admin key");
    sessionStorage.setItem(KEY_STORAGE, adminKey.trim());
    setUnlocked(true);
  };

  const lock = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    setAdminKey("");
    setUnlocked(false);
    setResult(null);
  };

  const finishResult = (data) => {
    setResult(data);
    if (data.candidates_synced > 0) {
      toast.success(`Synced ${data.candidates_synced} of ${data.with_profile ?? data.candidates_found} candidate(s)`);
    } else if ((data.with_profile ?? data.candidates_found) === 0) {
      toast.info("No matching candidates with a profile found");
    } else {
      toast.error(`Sync completed but 0 of ${data.with_profile} succeeded — check the report`);
    }
  };

  const pollJob = (jobId) => {
    const headers = { "X-API-Key": sessionStorage.getItem(KEY_STORAGE) };
    const tick = async () => {
      try {
        const r = await api.get(`/admin/backfill-status/${jobId}`, { headers });
        setResult({ ...r.data, results: r.data.failures || [] });
        if (r.data.status === "completed") {
          finishResult({ ...r.data, results: r.data.failures || [] });
          setRunning(false);
          return;
        }
        if (r.data.status === "error") {
          toast.error("Re-sync job failed — check server logs");
          setRunning(false);
          return;
        }
        setTimeout(tick, 2500);
      } catch (e) {
        toast.error(formatApiError(e));
        setRunning(false);
      }
    };
    setTimeout(tick, 2000);
  };

  const runRecovery = async (days) => {
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post(`/admin/backfill-recent-candidates?days=${days}`, null, {
        headers: { "X-API-Key": sessionStorage.getItem(KEY_STORAGE) },
      });
      if (r.data.mode === "all" && r.data.job_id) {
        toast.info("Full re-sync started — this runs in the background");
        pollJob(r.data.job_id);
        return; // keep running=true while polling
      }
      finishResult(r.data);
      setRunning(false);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Invalid admin key");
        lock();
      } else {
        toast.error(formatApiError(e));
      }
      setRunning(false);
    }
  };

  const runReparse = async () => {
    if (!window.confirm("Re-parse EVERY candidate's uploaded CV and re-sync them to the CRM? This can take a while.")) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/admin/reparse-all-cvs", null, { headers: { "X-API-Key": sessionStorage.getItem(KEY_STORAGE) } });
      toast.info("CV re-parse started — running in the background");
      pollJob(r.data.job_id);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Invalid admin key"); lock(); }
      else { toast.error(formatApiError(e)); }
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f3ee] px-4 py-16" data-testid="admin-page">
      <div className="mx-auto max-w-2xl">
        <div className="mb-10 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0d2f4d] text-white">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#0d2f4d]">Admin Console</h1>
            <p className="text-sm text-[#51637a]">The Ditch Careers — internal tools</p>
          </div>
        </div>

        {!unlocked ? (
          <div className="rounded-2xl border border-[#e3e0d3] bg-white p-8 shadow-sm" data-testid="admin-unlock">
            <div className="mb-4 flex items-center gap-2 text-[#0d2f4d]">
              <KeyRound className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-[0.15em]">Restricted access</span>
            </div>
            <label className="text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]">Admin key</label>
            <input
              type="password"
              data-testid="admin-key-input"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && unlock()}
              placeholder="Paste your admin key"
              className="mt-1.5 w-full rounded-lg border border-[#e3e0d3] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]"
            />
            <button
              onClick={unlock}
              data-testid="admin-unlock-btn"
              className="mt-5 w-full rounded-lg bg-[#0d2f4d] px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-[#0a2540]"
            >
              Unlock
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <AdManager />
            <section className="rounded-2xl border border-[#e3e0d3] bg-white p-8 shadow-sm" data-testid="sync-recovery-section">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[#0d2f4d]">Sync Recovery</h2>
                <button onClick={lock} className="text-xs text-[#51637a] underline-offset-2 hover:underline" data-testid="admin-lock-btn">
                  Lock console
                </button>
              </div>
              <p className="mt-2 max-w-lg text-sm text-[#51637a]">
                Re-push candidates to the Ditch Collective CRM with their full profile and uploaded
                documents (CVs, industry, experience). Use the 30-day option to recover recent signups,
                or run a full re-sync of every candidate in the database.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => runRecovery(30)}
                  disabled={running}
                  data-testid="recover-recent-signups-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-[#94a01a] px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-[#7f8a16] disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${running ? "animate-spin" : ""}`} />
                  {running ? "Recovering…" : "Recover Recent Signups (30 days)"}
                </button>
                <button
                  onClick={() => { if (window.confirm("Re-sync EVERY candidate in the database to the CRM? This may take a while.")) runRecovery(0); }}
                  disabled={running}
                  data-testid="resync-all-candidates-btn"
                  className="inline-flex items-center gap-2 rounded-lg border border-[#0d2f4d] bg-white px-5 py-3 text-sm font-medium text-[#0d2f4d] transition-colors hover:bg-[#0d2f4d] hover:text-white disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${running ? "animate-spin" : ""}`} />
                  {running ? "Re-syncing…" : "Re-sync ALL Candidates"}
                </button>
                <button
                  onClick={runReparse}
                  disabled={running}
                  data-testid="reparse-all-cvs-btn"
                  className="inline-flex items-center gap-2 rounded-lg border border-[#7C8514] bg-white px-5 py-3 text-sm font-medium text-[#5c6410] transition-colors hover:bg-[#7C8514] hover:text-white disabled:opacity-60"
                >
                  <FileText className={`h-4 w-4 ${running ? "animate-pulse" : ""}`} />
                  {running ? "Re-parsing…" : "Re-parse All CVs"}
                </button>
              </div>
            </section>

            {result && (
              <section className="rounded-2xl border border-[#e3e0d3] bg-white p-8 shadow-sm" data-testid="sync-recovery-result">
                {result.status === "running" ? (
                  <>
                    <div className="flex items-center gap-2 text-[#0d2f4d]">
                      <RefreshCw className="h-5 w-5 animate-spin text-[#94a01a]" />
                      <h3 className="text-base font-semibold">Full re-sync in progress…</h3>
                    </div>
                    <p className="mt-1 text-xs text-[#51637a]" data-testid="sync-progress">
                      Processed {result.processed ?? 0} of {result.candidates_found ?? "…"} ·
                      {" "}{result.candidates_synced ?? 0} synced · {result.failed ?? 0} failed
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-[#0d2f4d]">
                      <CheckCircle2 className="h-5 w-5 text-[#94a01a]" />
                      <h3 className="text-base font-semibold">
                        Synced {result.candidates_synced} of {result.with_profile ?? result.candidates_found} candidate(s)
                      </h3>
                    </div>
                    <p className="mt-1 text-xs text-[#51637a]">
                      Scope: {result.mode === "reparse" ? "CV re-parse — all candidates"
                        : result.mode === "all" ? "entire database" : `last ${result.window_days} days`}
                      {typeof result.failed === "number" ? ` · ${result.failed} failed` : ""}
                      {typeof result.skipped_no_profile === "number" ? ` · ${result.skipped_no_profile} without profile skipped` : ""}
                    </p>

                    {result.results?.length > 0 ? (
                      <div className="mt-5 divide-y divide-[#f0ede4]" data-testid="sync-recovery-list">
                        {result.results.map((c, i) => (
                          <div key={i} className="flex items-center justify-between py-3 text-sm">
                            <div>
                              <p className="font-medium text-[#0d2f4d]">{c.email}</p>
                              <p className="text-xs text-[#51637a]">{c.via}</p>
                            </div>
                            {c.synced ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-[#94a01a]">
                                <CheckCircle2 className="h-4 w-4" /> Synced
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                                <XCircle className="h-4 w-4" /> Failed
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-4 text-sm text-[#51637a]">
                        {result.mode === "all"
                          ? "All candidates synced successfully — no failures."
                          : "No matching candidates with a profile were found in this environment."}
                      </p>
                    )}
                  </>
                )}
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
